<script lang="ts">
  /**
   * Starting a room, as a screen of its own: the shell's `PickerPage` in its
   * `screen` frame (drawn in place under a back chevron; `App.svelte` owns the
   * history entry, which the room it creates then takes over;
   * `docs/decisions/0054`). Which duel (or which co-op), its goal, how hard,
   * and for the computer how good it is. Opened by one of the Together tab's
   * three tiles, and the tile decides the kind: there is no switch between a
   * duel, co-op and the computer in here.
   *
   * Every choice with a sentence to it is a card that carries its own
   * explanation, so nothing needs a "what's this?" beside it. The goal and
   * its target are short words, so they are pill segments at the right of a
   * row. The handicap is a switch.
   *
   * The name is asked here, where a seat is about to be taken, and only of a
   * guest (`nav/PlayAsField.svelte`).
   *
   * Start (the template's, always in view) names what it will do. Creating a
   * room goes straight into the room: `App.svelte` swaps this screen for the
   * room under the same history entry, so Back from the lobby returns to the
   * Together tab.
   */
  import { untrack } from 'svelte';

  import {
    Group,
    PickerPage,
    PillButton,
    RadioCards,
    Row,
    SectionLabel,
    SegmentedControl,
    ToggleRow,
  } from '@artibens/app-shell';
  import type { SegmentOption } from '@artibens/app-shell';
  import {
    COOP_VARIANTS,
    DEFAULT_AI_LEVEL,
    DEFAULT_COOP_VARIANT,
    DEFAULT_DUEL_VARIANT,
    DUEL_VARIANTS,
    aiLevelName,
    duelVariantName,
    isCoopVariant,
  } from '@interlock/protocol';
  import type { AiLevel, DuelRules, RoomVariant } from '@interlock/protocol';
  import {
    DEFAULT_HARDNESS,
    SPRINT_DEFAULT_LINE_GOAL,
    SPRINT_LINE_GOALS,
    SPRINT_TIME_GOALS_MIN,
  } from '@interlock/modes';
  import type { Hardness } from '@interlock/modes';

  import { startAudioForGesture } from '../audio/bootstrap.js';
  import { explainAiHandicap, explainHandicap } from '../copy/explanations.js';
  import { aiLevelCards, hardnessCards, variantCards } from '../copy/pickers.js';
  import ExplanationSheet from '../nav/ExplanationSheet.svelte';
  import PlayAsField from '../nav/PlayAsField.svelte';
  import { createRoom } from '../net/connect.js';
  import { account } from '../state/account.svelte.js';
  import { loadGuestName, saveGuestName } from '../state/guest-name-io.js';

  const {
    preset,
    onBack,
    onJoin,
    error: joinError = '',
  }: {
    preset: 'ai' | 'duel' | 'coop';
    onBack: () => void;
    /** Enters the room that was just created. Settles when the join has been tried. */
    onJoin: (request: {
      roomId: string;
      code: string;
      name: string;
      role: 'player' | 'spectator';
    }) => Promise<void>;
    /** Why entering the room failed, from `App.svelte`. */
    error?: string;
  } = $props();

  type GoalKind = 'lines' | 'time';

  const goalOptions: SegmentOption<GoalKind>[] = [
    { value: 'lines', label: 'Lines' },
    { value: 'time', label: 'Timed' },
  ];
  const lineOptions: SegmentOption[] = SPRINT_LINE_GOALS.map((n) => ({
    value: String(n),
    label: String(n),
  }));
  const timeOptions: SegmentOption[] = SPRINT_TIME_GOALS_MIN.map((n) => ({
    value: String(n),
    label: `${String(n)} min`,
  }));

  // The screen is mounted for one door and never changes it (`App.svelte`
  // mounts a new one per tile), so the starting variant is read once.
  const vsAi = $derived(preset === 'ai');
  const coop = $derived(preset === 'coop');

  let variant = $state<RoomVariant>(
    untrack(() => (preset === 'coop' ? DEFAULT_COOP_VARIANT : DEFAULT_DUEL_VARIANT)),
  );
  let hardness = $state<Hardness>(DEFAULT_HARDNESS);
  let goalKind = $state<GoalKind>('lines');
  let goalLines = $state(SPRINT_DEFAULT_LINE_GOAL);
  let goalTimeMin = $state(2);
  let handicap = $state(true);
  let aiLevel = $state<AiLevel>(DEFAULT_AI_LEVEL);
  let name = $state(account.player?.username ?? loadGuestName());
  let busy = $state(false);
  let error = $state('');

  let handicapExplainOpen = $state(false);
  let aiHandicapExplainOpen = $state(false);

  const hasGoal = $derived(variant === 'duelSprint' || isCoopVariant(variant));
  const timedGoal = $derived(variant === 'duelSprint' && goalKind === 'time');
  const risingFloor = $derived(variant === 'duelSurvival' || variant === 'coopWide');

  const rules = $derived<DuelRules>(
    !hasGoal
      ? { variant, hardness }
      : {
          variant,
          hardness,
          goal: timedGoal
            ? { kind: 'time', ms: goalTimeMin * 60_000 }
            : { kind: 'lines', lines: goalLines },
        },
  );

  const title = $derived(vsAi ? 'Play the computer' : coop ? 'Start a co-op game' : 'Start a duel');
  const submitLabel = $derived(
    vsAi
      ? `Play the computer · ${aiLevelName(aiLevel)}`
      : `Create room · ${duelVariantName(variant)}`,
  );
  const nameLooksRight = $derived(name.trim().length > 0);
  const shownError = $derived(error !== '' ? error : joinError);

  async function host(): Promise<void> {
    if (!nameLooksRight || busy) return;
    // The AudioContext may only start on a user gesture; this tap is it.
    startAudioForGesture();
    busy = true;
    error = '';
    try {
      saveGuestName(name);
      const room = await createRoom(
        rules,
        preset === 'duel' ? handicap : undefined,
        vsAi ? aiLevel : undefined,
      );
      await onJoin({ roomId: room.roomId, code: room.code, name: name.trim(), role: 'player' });
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not reach the server.';
    } finally {
      busy = false;
    }
  }
</script>

{#snippet goalControl()}
  <SegmentedControl
    label="Goal type"
    value={goalKind}
    options={goalOptions}
    onchange={(v) => {
      goalKind = v;
    }}
  />
{/snippet}

{#snippet targetControl()}
  {#if !timedGoal}
    <SegmentedControl
      label="Line target"
      value={String(goalLines)}
      options={lineOptions}
      onchange={(v) => {
        goalLines = Number(v);
      }}
    />
  {:else}
    <SegmentedControl
      label="Time limit"
      value={String(goalTimeMin)}
      options={timeOptions}
      onchange={(v) => {
        goalTimeMin = Number(v);
      }}
    />
  {/if}
{/snippet}

{#snippet sections()}
  <SectionLabel variant="picker" as="h2">{coop ? 'Which co-op?' : 'Which duel?'}</SectionLabel>
  <RadioCards
    label="Variant"
    value={variant}
    options={variantCards(coop ? COOP_VARIANTS : DUEL_VARIANTS)}
    chosenTaglineTestid="variant-tagline"
    onchange={(v) => {
      variant = v;
    }}
  />

  {#if hasGoal}
    <SectionLabel variant="picker" as="h2">The goal</SectionLabel>
    <Group>
      {#if variant === 'duelSprint'}
        <Row label="Goal" trailing={goalControl} />
      {/if}
      <Row label={timedGoal ? 'Limit' : 'Target'} trailing={targetControl} />
    </Group>
  {/if}

  <SectionLabel variant="picker" as="h2">How hard?</SectionLabel>
  <RadioCards
    label="Hardness"
    value={hardness}
    options={hardnessCards(risingFloor)}
    onchange={(v) => {
      hardness = v;
    }}
  />

  {#if vsAi}
    <SectionLabel variant="picker" as="h2">How good is it?</SectionLabel>
    <RadioCards
      label="AI difficulty"
      value={aiLevel}
      options={aiLevelCards()}
      onchange={(v) => {
        aiLevel = v;
      }}
    />
    <div class="explain">
      <PillButton
        variant="plain"
        label="Why there is no handicap here"
        onclick={() => {
          aiHandicapExplainOpen = true;
        }}
      />
    </div>
  {:else if preset === 'duel'}
    <SectionLabel variant="picker" as="h2">Handicap</SectionLabel>
    <Group>
      <ToggleRow
        label="Handicap"
        detail="Eases gravity and incoming attack for whoever is behind"
        checked={handicap}
        onchange={(next) => {
          handicap = next;
        }}
        testid="handicap-toggle"
      />
    </Group>
    <div class="explain">
      <PillButton
        variant="plain"
        label="How the ease is decided"
        onclick={() => {
          handicapExplainOpen = true;
        }}
      />
    </div>
  {/if}

  <SectionLabel variant="picker" as="h2">Your seat</SectionLabel>
  <PlayAsField bind:name onenter={() => void host()} />
{/snippet}

<PickerPage
  frame="screen"
  {title}
  onclose={onBack}
  {sections}
  note={vsAi
    ? 'One tap and you are playing. Results against the computer are kept apart from the global records.'
    : 'You get the code on the next screen.'}
  error={shownError === '' ? null : shownError}
  start={{
    label: submitLabel,
    disabled: !nameLooksRight,
    busy,
    testid: 'create-room-submit',
    onclick: () => void host(),
  }}
  testid="create-room-sheet"
  testids={{ error: 'create-room-error' }}
/>

<ExplanationSheet
  open={aiHandicapExplainOpen}
  label="No handicap against the computer"
  text={explainAiHandicap()}
  onclose={() => {
    aiHandicapExplainOpen = false;
  }}
/>
<ExplanationSheet
  open={handicapExplainOpen}
  label="The handicap"
  text={explainHandicap()}
  onclose={() => {
    handicapExplainOpen = false;
  }}
/>

<style>
  /* A way to read more, centred under the question it explains. */
  .explain {
    display: flex;
    justify-content: center;
    margin-top: var(--ui-space-2);
  }
</style>
